import {Button, ControlledDatepicker, ControlledSelect, GenericInput} from '@hm/fundamental'
import type {ControlledDatepickerValue} from '@hm/fundamental'
import {useCallback, useEffect} from 'react'
import {useForm} from 'react-hook-form'

import {fromUtcDateOnly, toUtcDateOnlyIso} from '../../helpers'
import {useRiskSurveyTranslation, useUpdateRiaDetails, useUpdateRiaStatus} from '../../hooks'
import {ResponsibleRole, RiaStatus} from '../../types'

import {getAvailableStatuses} from './statusUpdate.helpers'

type RiaEditFormProps = {
  riaId: string
  currentStatus: RiaStatus
  currentResponsiblePerson?: ResponsibleRole | null
  currentDueDate?: string | null
  onDone: () => void
}

type FormValues = {
  status: RiaStatus
  responsiblePerson: ResponsibleRole | ''
  dueDate: Date | null
  reason: string
}

const toDefaults = (
  status: RiaStatus,
  responsiblePerson?: ResponsibleRole | null,
  dueDate?: string | null
): FormValues => ({
  status,
  responsiblePerson: responsiblePerson ?? '',
  dueDate: dueDate ? fromUtcDateOnly(dueDate) : null,
  reason: ''
})

// Single edit surface for the three manager-editable fields. Status and details
// live behind separate endpoints, so Save dispatches only the mutations whose
// value actually changed (status is never re-sent when unchanged to avoid a
// spurious audit entry + notification).
export const RiaEditForm = ({
  riaId,
  currentStatus,
  currentResponsiblePerson,
  currentDueDate,
  onDone
}: RiaEditFormProps) => {
  const {t} = useRiskSurveyTranslation()
  const {mutateAsync: updateStatus, isPending: statusPending} = useUpdateRiaStatus()
  const {mutateAsync: updateDetails, isPending: detailsPending} = useUpdateRiaDetails()
  const isPending = statusPending || detailsPending

  const {watch, setValue, reset, handleSubmit} = useForm<FormValues>({
    defaultValues: toDefaults(currentStatus, currentResponsiblePerson, currentDueDate)
  })

  const selectedStatus = watch('status')
  const selectedResponsible = watch('responsiblePerson')
  const dueDate = watch('dueDate')
  const reason = watch('reason')

  const statusChanged = selectedStatus !== currentStatus
  const reasonRequired = statusChanged && selectedStatus === RiaStatus.Rejected
  const reasonMissing = reasonRequired && !reason?.trim()

  useEffect(() => {
    reset(toDefaults(currentStatus, currentResponsiblePerson, currentDueDate))
  }, [riaId, currentStatus, currentResponsiblePerson, currentDueDate, reset])

  const statusOptions = [
    {value: currentStatus, label: t(`status.${currentStatus}`)},
    ...getAvailableStatuses(currentStatus).map((status) => ({
      value: status,
      label: t(`status.${status}`)
    }))
  ]

  const responsibleOptions = Object.values(ResponsibleRole).map((role) => ({
    value: role,
    label: t(`responsibleRole.${role}`)
  }))

  const handleDueDateChange = useCallback(
    (value: ControlledDatepickerValue) => {
      const next = Array.isArray(value) ? value[0] : value
      setValue('dueDate', next ?? null)
    },
    [setValue]
  )

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const statusHasChanged = values.status !== currentStatus
      // Compare due dates by calendar day (UTC-anchored) so a timezone shift never
      // registers as a change or re-sends an unchanged value.
      const currentDueIso = currentDueDate
        ? toUtcDateOnlyIso(fromUtcDateOnly(currentDueDate))
        : null
      const newDueIso = values.dueDate ? toUtcDateOnlyIso(values.dueDate) : null
      const detailsHaveChanged =
        (values.responsiblePerson || null) !== (currentResponsiblePerson ?? null) ||
        currentDueIso !== newDueIso

      if (statusHasChanged && values.status === RiaStatus.Rejected && !values.reason?.trim()) {
        return
      }

      try {
        if (detailsHaveChanged) {
          await updateDetails({
            riaId,
            data: {
              responsiblePerson: values.responsiblePerson || null,
              dueDate: values.dueDate ? toUtcDateOnlyIso(values.dueDate) : null
            }
          })
        }
        if (statusHasChanged) {
          await updateStatus({
            riaId,
            data: {status: values.status, reason: values.reason?.trim() || null}
          })
        }
        onDone()
      } catch {
        // The mutation hooks surface their own error toasts; keep the form open on failure.
      }
    },
    [
      riaId,
      currentStatus,
      currentResponsiblePerson,
      currentDueDate,
      updateDetails,
      updateStatus,
      onDone
    ]
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
      <ControlledSelect
        name="status"
        label={t('detail.status')}
        value={selectedStatus}
        onChange={(value) => setValue('status', value as RiaStatus)}
        values={statusOptions}
        data-cy="status-select"
      />
      {reasonRequired && (
        <GenericInput
          label={t('detail.reason')}
          value={reason}
          onChange={(value) => setValue('reason', value)}
          state={reasonMissing ? 'error' : undefined}
          error={reasonMissing ? t('detail.reasonRequired') : undefined}
          data-cy="status-reason-input"
        />
      )}
      <ControlledSelect
        name="responsiblePerson"
        label={t('detail.responsiblePerson')}
        value={selectedResponsible}
        onChange={(value) => setValue('responsiblePerson', value as ResponsibleRole | '')}
        values={[{value: '', label: t('detail.selectResponsiblePerson')}, ...responsibleOptions]}
        data-cy="responsible-person-select"
      />
      <ControlledDatepicker
        name="dueDate"
        label={t('detail.dueDate')}
        value={dueDate}
        onChange={handleDueDateChange}
        data-cy="due-date-picker"
      />
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          size="large"
          disabled={isPending}
          onClick={onDone}
          data-cy="edit-cancel-button"
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          size="large"
          disabled={isPending || reasonMissing}
          data-cy="edit-save-button"
        >
          {t('detail.save')}
        </Button>
      </div>
    </form>
  )
}
